import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import type { RegionData, RegionSetData } from '@/models/Regions'

type LocationMode = 'onboarding' | 'settings' | 'search'

type LocationItem = {
  id: string
  name: string
  ids: number[]
  major?: string
  suburb?: string
}

type CouncilGroup = {
  id: string
  name: string
  ids: number[]
  majors: LocationItem[]
  suburbsByMajor: Map<string, LocationItem[]>
  // Ids to store when the whole group is selected and it has no marker row of
  // its own (Auckland area groups): the set's member rows — town-level majors,
  // which the matching code wildcards, plus the metro suburbs. Suburbs beneath a
  // held town are deliberately NOT materialised.
  wholeIds?: number[]
}

type RegionGroup = {
  name: string
  type: 'councils' | 'majors' | 'region_sets' | 'region'
  note: string
  items: LocationItem[]
  councils: CouncilGroup[]
  // Region-level ids selected directly by the tile when a region has no
  // councils to refine (e.g. Chatham Islands). Empty for normal regions.
  directIds: number[]
  // Legacy "whole region" row ids (region set, council/major/suburb NULL) for a
  // region that DOES have councils. The modern picker never stores these — it
  // stores council ids — but ~260 existing orgs still hold them. We surface them
  // so the selection is visible and the user can refine it to councils. Empty
  // for flat regions (those use directIds instead).
  regionLevelIds: number[]
  // Auckland's "Auckland" major row: matching reads it as every metro suburb. No
  // tile offers it, but an admin can attach it, so it is shown and clearable.
  metroIds?: number[]
}

interface LocationSelectorProps {
  regions: RegionData[]
  regionSets?: RegionSetData[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
  mode: LocationMode
  showCounter?: boolean
  opportunityCount?: number
  opportunityCountLoading?: boolean
}

// Top-level "regions" hidden from the picker entirely. "Area Outside Region" is
// a Statistics NZ catch-all for territory outside every regional council; no
// funder or opportunity targets it, so it only confuses users. We keep the DB
// row (so any existing selection stays valid) but never surface it as selectable.
const HIDDEN_REGION_NAMES = new Set(['Area Outside Region'])

// The nine customer-facing Auckland area groups. Auckland is one unitary council, so
// the picker subdivides it by these curated region sets instead of by council. They
// are pinned BY NAME: membership still comes from the API (so membership fixes need
// no deploy), but only these nine ever become tiles. Any other Auckland-heavy set
// (local board sets, North Harbour, …) is admin vocabulary and must not surface
// here. Names must match region_sets.name exactly; the admin refuses to rename them.
export const AUCKLAND_PICKER_SETS = [
  'Central Auckland',
  'East Auckland',
  'South Auckland',
  'West Auckland',
  'North Shore',
  'Rodney',
  'Franklin (Auckland)',
  'Hibiscus Coast',
  'Hauraki Gulf Islands',
] as const

const reportedPickerGaps = new Set<string>()

// A picker set that no longer matches by name, or has no members, drops its tile and
// every place under it, silently. Skipped while the sets are still loading, and
// reported once per page load.
function reportPickerGaps(regionSets: RegionSetData[], missing: string[]) {
  if (regionSets.length === 0 || missing.length === 0) return
  const message = `Auckland picker sets missing or empty: ${missing.join(', ')}`
  if (reportedPickerGaps.has(message)) return
  reportedPickerGaps.add(message)
  Sentry.captureMessage(message, 'warning')
}

const SINGLE_COUNCIL_MAJOR_REGIONS = new Set([
  'Marlborough',
  'Tasman',
  'Tairawhiti Gisborne',
  'Tairāwhiti Gisborne',
  'Nelson',
])

function uniq(nums: number[]) {
  return [...new Set(nums)]
}

function withoutIds(selectedIds: number[], idsToRemove: number[]) {
  const remove = new Set(idsToRemove)
  return selectedIds.filter((id) => !remove.has(id))
}

function hasAny(selectedIds: number[], ids: number[]) {
  const selected = new Set(selectedIds)
  return ids.some((id) => selected.has(id))
}

function hasAll(selectedIds: number[], ids: number[]) {
  const selected = new Set(selectedIds)
  return ids.every((id) => selected.has(id))
}

// Ids that represent a *committed* selection (council/major/suburb or a flat
// region's direct ids) — everything except the legacy region-level ids.
function committedIdsForGroup(group: RegionGroup) {
  const ids = [...group.directIds, ...group.items.flatMap((item) => item.ids)]
  group.councils.forEach((council) => {
    ids.push(...council.ids)
    ids.push(...council.majors.flatMap((major) => major.ids))
    council.suburbsByMajor.forEach((suburbs) => {
      ids.push(...suburbs.flatMap((suburb) => suburb.ids))
    })
  })
  return uniq(ids)
}

function idsForGroup(group: RegionGroup) {
  return uniq([...committedIdsForGroup(group), ...group.regionLevelIds, ...(group.metroIds ?? [])])
}

// Ids that select real areas of the region, as opposed to its region-level row.
function areaIdsForGroup(group: RegionGroup) {
  return [...committedIdsForGroup(group), ...(group.metroIds ?? [])]
}

function isRegionLevelOnly(group: RegionGroup, selectedIds: number[]) {
  return hasAny(selectedIds, group.regionLevelIds) && !hasAny(selectedIds, areaIdsForGroup(group))
}

// Removing the region's last area leaves it region-only rather than dropping
// the region; only Remove takes the region out.
function keepRegion(group: RegionGroup, next: number[]) {
  return !hasAny(next, areaIdsForGroup(group)) && group.regionLevelIds.length > 0
    ? uniq([...next, ...group.regionLevelIds])
    : next
}

// Auckland's region box lists area tiles and single-council regions list towns, so
// "councils" fits only regions whose box lists councils.
function RegionOnlyNotice({ group }: { group: RegionGroup }) {
  const parts = group.type === 'councils' ? 'councils' : 'areas'
  return (
    <Alert severity="warning">
      <strong>{group.name} region only.</strong> You'll only be matched with funders that fund the whole {group.name} region. Funders that target specific {parts} or suburbs <strong>won't</strong> match unless you select them. Tick the {parts} you serve to include local funding.
    </Alert>
  )
}

// Ids to add when selecting a whole council. Normal councils carry a single
// council-level marker (council.ids); Auckland set groups have none, so we
// materialise every leaf instead.
function wholeCouncilIds(council: CouncilGroup) {
  if (council.ids.length) return council.ids
  return council.wholeIds ?? idsForCouncil(council)
}

function idsForCouncil(council: CouncilGroup) {
  const ids = [...council.ids, ...council.majors.flatMap((major) => major.ids)]
  council.suburbsByMajor.forEach((suburbs) => {
    ids.push(...suburbs.flatMap((suburb) => suburb.ids))
  })
  return uniq(ids)
}

// True when every area of the council is selected — whether represented by the
// whole-council marker, individually-selected majors, or all of a major's
// suburbs ticked. Used to decide "Refined": a council is only refined when its
// selection is a STRICT subset of its areas, not when everything happens to be
// ticked individually.
function isCouncilFullySelected(council: CouncilGroup, selectedIds: number[]) {
  const selected = new Set(selectedIds)
  if (council.ids.some((id) => selected.has(id))) return true
  if (council.majors.length === 0) return false
  return council.majors.every((major) => {
    if (major.ids.some((id) => selected.has(id))) return true
    const suburbs = council.suburbsByMajor.get(major.major ?? '') ?? []
    if (suburbs.length === 0) return false
    return suburbs.every((suburb) => suburb.ids.every((id) => selected.has(id)))
  })
}

function suburbsForMajor(council: CouncilGroup, major: LocationItem) {
  return council.suburbsByMajor.get(major.major ?? '') ?? []
}

function idsForMajor(council: CouncilGroup, major: LocationItem) {
  return uniq([
    ...major.ids,
    ...suburbsForMajor(council, major).flatMap((suburb) => suburb.ids),
  ])
}

export function isNational(region: RegionData) {
  return (
    region.name === 'New Zealand' ||
    (region.country === 'New Zealand' &&
      !region.region &&
      !region.council &&
      !region.major &&
      !region.suburb)
  )
}

function findLevelRegion(
  regions: RegionData[],
  regionName: string,
  council?: string,
  major?: string,
  suburb?: string,
) {
  return regions.find((region) =>
    region.region === regionName &&
    (council === undefined ? !region.council : region.council === council) &&
    (major === undefined ? !region.major : region.major === major) &&
    (suburb === undefined ? !region.suburb : region.suburb === suburb)
  )
}

function groupBy<T>(items: T[], getKey: (item: T) => string | undefined) {
  const map = new Map<string, T[]>()
  items.forEach((item) => {
    const key = getKey(item)
    if (!key) return
    map.set(key, [...(map.get(key) ?? []), item])
  })
  return map
}

// A curated Auckland area group (Central, Rodney, …) → a council-like group that
// nests town → suburb, so Auckland refines the way every other region does
// (area group → town → suburb). The set's member rows are of two kinds:
//   * town-level majors (Warkworth, Pukekohe, Waiheke Island …), whose suburbs are
//     read from the taxonomy and listed beneath them, exactly as a council's majors
//     are; ticking the town stores its major row, which the matching code wildcards;
//   * metro suburbs (major "Auckland"), which cannot nest under their own major
//     because "Auckland" spans five area groups. They are grouped under one heading
//     named after the tile ("Central Auckland suburbs"). That heading has no id of
//     its own — the "Auckland" major row means all of metropolitan Auckland and is
//     never selected from inside one area group — so ticking it selects the tile's
//     suburbs. A tile with no towns lists its suburbs directly, since a heading
//     would be its only child and cost an extra Refine.
// No whole-set marker row exists, so ids is empty and selecting the whole tile
// stores wholeIds: the member rows themselves (towns + metro suburbs).
function buildSetCouncil(set: RegionSetData, aucklandRows: RegionData[]): CouncilGroup {
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name)
  const memberIds = new Set(set.region_ids)
  const members = aucklandRows.filter((region) => region.major && memberIds.has(region.id))
  const towns = members.filter((region) => !region.suburb).sort((a, b) => a.major!.localeCompare(b.major!))
  const townMajors = new Set(towns.map((town) => town.major!))
  const majors: LocationItem[] = []
  const suburbsByMajor = new Map<string, LocationItem[]>()

  const metroRows = members.filter((region) => region.suburb && !townMajors.has(region.major!))
  if (metroRows.length > 0 && towns.length === 0) {
    metroRows
      .map((region) => ({ id: `${set.name}-${region.id}`, name: region.suburb!, ids: [region.id], major: `${set.name}-${region.id}` }))
      .sort(byName)
      .forEach((item) => majors.push(item))
  } else if (metroRows.length > 0) {
    const key = `${set.name}-suburbs`
    majors.push({ id: key, name: `${set.name} suburbs`, ids: [], major: key })
    suburbsByMajor.set(
      key,
      metroRows
        .map((region) => ({ id: `${key}-${region.id}`, name: region.suburb!, ids: [region.id], major: key, suburb: region.suburb! }))
        .sort(byName),
    )
  }

  towns.forEach((town) => {
    const key = `${set.name}-${town.major}`
    majors.push({ id: key, name: town.major!, ids: [town.id], major: key })
    suburbsByMajor.set(
      key,
      aucklandRows
        .filter((region) => region.suburb && region.major === town.major)
        .map((region) => ({ id: `${key}-${region.id}`, name: region.suburb!, ids: [region.id], major: key, suburb: region.suburb! }))
        .sort(byName),
    )
  })

  return {
    id: set.name,
    name: set.name,
    ids: [],
    majors,
    suburbsByMajor,
    wholeIds: members.map((region) => region.id),
  }
}

export function buildLocationGroups(
  regions: RegionData[],
  regionSets: RegionSetData[] = [],
): { nationalRegion?: RegionData; groups: RegionGroup[] } {
  const nationalRegion = regions.find(isNational)
  const nzRegions = regions.filter(
    (region) =>
      region.country === 'New Zealand' &&
      region.region &&
      !HIDDEN_REGION_NAMES.has(region.region),
  )
  const regionNames = [...new Set(nzRegions.map((region) => region.region!).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))

  const groups = regionNames.map((regionName) => {
    const rows = nzRegions.filter((region) => region.region === regionName)
    // Legacy whole-region rows (no council/major/suburb) — the picker no longer
    // creates these, but existing orgs may still have them selected.
    const regionLevelIds = rows
      .filter((region) => !region.council && !region.major && !region.suburb)
      .map((region) => region.id)
    const councilRows = groupBy(rows, (region) => region.council)
    const councils = [...councilRows.entries()].map(([councilName, councilRegions]) => {
      const councilLevel = findLevelRegion(regions, regionName, councilName)
      const majorRows = groupBy(councilRegions, (region) => region.major)
      const majors = [...majorRows.entries()].map(([majorName, majorRegions]) => {
        const majorLevel = findLevelRegion(regions, regionName, councilName, majorName)
        if (!majorLevel) {
          console.warn(`Missing major-level region row for ${regionName} / ${councilName} / ${majorName}`)
        }
        return {
          id: `${councilName}-${majorName}`,
          name: majorName,
          ids: majorLevel ? [majorLevel.id] : [],
          major: majorName,
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
      const suburbsByMajor = new Map<string, LocationItem[]>()
      majorRows.forEach((majorRegions, majorName) => {
        suburbsByMajor.set(
          majorName,
          majorRegions
            .filter((region) => region.suburb)
            .map((region) => ({
              id: `${majorName}-${region.suburb}`,
              name: region.suburb!,
              ids: [region.id],
              major: majorName,
              suburb: region.suburb!,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        )
      })

      return {
        id: councilName,
        name: councilName,
        ids: [councilLevel?.id ?? councilRegions[0].id],
        majors,
        suburbsByMajor,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))

    // Flat region with no councils to refine (e.g. Chatham Islands): the tile
    // selects the region-level id directly, with no expandable detail.
    if (councils.length === 0) {
      return {
        name: regionName,
        type: 'region' as const,
        note: '',
        items: [],
        councils: [],
        directIds: rows.map((region) => region.id),
        regionLevelIds: [],
      }
    }

    if (regionName === 'Auckland') {
      // Auckland is a single unitary council, so the curated region sets (Central,
      // West, Rodney, …) are the natural navigation — not the lone "Auckland
      // Council". Each set is a clickable group that refines town → suburb like a
      // council does. Only the nine sets in AUCKLAND_PICKER_SETS become tiles,
      // matched by name and shown in that order. Every Auckland leaf row is covered
      // by exactly one of the nine (directly, or via its town's major row). Admin
      // writes refuse to break that, so there is no catch-all: a row outside the
      // nine is a data defect to fix in the sets, not something to show.
      const pinned = new Map(regionSets.map((set) => [set.name, set] as const))
      const tiles = AUCKLAND_PICKER_SETS.map((name) => {
        const set = pinned.get(name)
        return set ? buildSetCouncil(set, rows) : undefined
      })
      reportPickerGaps(regionSets, AUCKLAND_PICKER_SETS.filter((_, i) => !tiles[i]?.majors.length))
      const aucklandCouncils = tiles.filter((council): council is CouncilGroup => !!council?.majors.length)
      return {
        name: regionName,
        type: 'region_sets' as const,
        note: "All pre-selected - uncheck areas where you don't work.",
        items: aucklandCouncils.map((council) => ({
          id: council.id,
          name: council.name,
          ids: wholeCouncilIds(council),
        })),
        councils: aucklandCouncils,
        directIds: [],
        regionLevelIds,
        metroIds: rows.filter((region) => region.major === 'Auckland' && !region.suburb).map((region) => region.id),
      }
    }

    const isSingleCouncilMajorRegion = SINGLE_COUNCIL_MAJOR_REGIONS.has(regionName)
    return {
      name: regionName,
      type: isSingleCouncilMajorRegion ? 'majors' as const : 'councils' as const,
      note: isSingleCouncilMajorRegion
        ? "All pre-selected - uncheck areas where you don't work."
        : "All pre-selected - uncheck councils where you don't work.",
      items: isSingleCouncilMajorRegion ? (councils[0]?.majors ?? []) : councils.map((council) => ({
        id: council.id,
        name: council.name,
        ids: council.ids,
      })),
      councils,
      directIds: [],
      regionLevelIds,
    }
  })

  return { nationalRegion, groups }
}

// ---------------------------------------------------------------------------
// "Locations Served" summary — collapse a raw region-id selection into the
// picker's own language. A wholly-selected node shows as just its name; a
// partial node shows the SHORTER of "except <removed>" (red) / "only <kept>"
// (green), recursing into any kept child that is itself partial.
// ---------------------------------------------------------------------------

// A summary works at the sub-area (region-set / council) level only: a refined
// sub-area is just "some of X" — we never enumerate suburbs. A near-whole region
// reads "Auckland, some of West Auckland"; a sparse one "Auckland: only ...".
type SubAreaRef = { name: string; partial: boolean }

export type LocationSummaryNode = {
  name: string
  only?: SubAreaRef[] // few sub-areas served → list them (refined ones as "some of")
  except?: string[] // few sub-areas fully off → name them, the rest are whole
  someOf?: string[] // refined sub-areas of an otherwise-whole region
}

type SelNode = { name: string; ids: number[]; markerIds: number[]; children: SelNode[] }

function selNodeIsSelected(node: SelNode, sel: Set<number>) {
  return node.ids.some((id) => sel.has(id)) || node.markerIds.some((id) => sel.has(id))
}

function selNodeIsWhole(node: SelNode, sel: Set<number>): boolean {
  if (node.markerIds.some((id) => sel.has(id))) return true
  if (node.children.length === 0) return node.ids.length > 0 && node.ids.every((id) => sel.has(id))
  return node.children.every((child) => selNodeIsWhole(child, sel))
}

function summariseRegion(region: SelNode, sel: Set<number>): LocationSummaryNode {
  if (region.children.length === 0 || selNodeIsWhole(region, sel)) return { name: region.name }
  const whole = region.children.filter((sub) => selNodeIsWhole(sub, sel))
  const partial = region.children.filter(
    (sub) => selNodeIsSelected(sub, sel) && !selNodeIsWhole(sub, sel),
  )
  const off = region.children.filter((sub) => !selNodeIsSelected(sub, sel))
  const served = whole.length + partial.length

  // More sub-areas off than served → list what IS served (refined ones as "some of").
  if (off.length >= served) {
    return {
      name: region.name,
      only: [
        ...whole.map((sub) => ({ name: sub.name, partial: false })),
        ...partial.map((sub) => ({ name: sub.name, partial: true })),
      ],
    }
  }
  // Otherwise the region is (near-)whole: name it, flag the few fully-off areas
  // as "except", and any refined areas as "some of".
  return {
    name: region.name,
    ...(off.length ? { except: off.map((sub) => sub.name) } : {}),
    ...(partial.length ? { someOf: partial.map((sub) => sub.name) } : {}),
  }
}

function majorSelNode(council: CouncilGroup, major: LocationItem): SelNode {
  return {
    name: major.name,
    ids: idsForMajor(council, major),
    markerIds: major.ids,
    children: suburbsForMajor(council, major).map((suburb) => ({
      name: suburb.name, ids: suburb.ids, markerIds: suburb.ids, children: [],
    })),
  }
}

function groupSelNode(group: RegionGroup): SelNode {
  if (group.type === 'region') {
    return { name: group.name, ids: group.directIds, markerIds: group.directIds, children: [] }
  }
  return {
    name: group.name,
    ids: idsForGroup(group),
    markerIds: group.regionLevelIds,
    children: group.councils.map((council) => ({
      name: council.name,
      ids: idsForCouncil(council),
      markerIds: council.ids,
      children: council.majors.map((major) => majorSelNode(council, major)),
    })),
  }
}

const METRO_AUCKLAND_LABEL = 'Metropolitan Auckland'

export function summariseLocations(
  regions: RegionData[],
  regionSets: RegionSetData[] | undefined,
  selectedIds: number[],
): { national: boolean; regions: LocationSummaryNode[] } {
  const sel = new Set(selectedIds)
  const { nationalRegion, groups } = buildLocationGroups(regions, regionSets ?? [])
  const regionSummaries = groups
    .map((group) => ({ group, node: groupSelNode(group) }))
    .filter(({ node }) => selNodeIsSelected(node, sel))
    .map(({ group, node }) => {
      const summary = summariseRegion(node, sel)
      const metroSelected = (group.metroIds ?? []).some((id) => sel.has(id))
      return metroSelected && summary.only
        ? { ...summary, only: [...summary.only, { name: METRO_AUCKLAND_LABEL, partial: false }] }
        : summary
    })
  return { national: !!nationalRegion && sel.has(nationalRegion.id), regions: regionSummaries }
}

function renderSummaryNode(node: LocationSummaryNode): ReactNode {
  const red = { color: '#b91c1c' }
  const green = { color: '#15803d' }
  // Label the top level as a region so "Wellington" isn't read as the city.
  const label = `${node.name} region`
  if (node.only && node.only.length) {
    const parts = node.only.map((ref) => (ref.partial ? `some of ${ref.name}` : ref.name)).join(', ')
    return <>{label}: <Box component="span" sx={green}>only {parts}</Box></>
  }
  const segments: ReactNode[] = []
  if (node.except && node.except.length) {
    segments.push(<Box component="span" sx={red}>except {node.except.join(', ')}</Box>)
  }
  if (node.someOf && node.someOf.length) {
    segments.push(<Box component="span" sx={green}>some of {node.someOf.join(', ')}</Box>)
  }
  if (!segments.length) return label
  return (
    <>
      {label}
      {segments.map((seg, index) => (
        // "except" reads as "Wellington region except X"; a lone "some of" wants a comma.
        <Fragment key={index}>{index === 0 && node.except ? ' ' : ', '}{seg}</Fragment>
      ))}
    </>
  )
}

// Renders an org/need region-id selection as a concise, human-readable summary.
export function LocationsServedSummary({
  regions,
  regionSets,
  selectedIds,
}: {
  regions?: RegionData[]
  regionSets?: RegionSetData[]
  selectedIds: number[]
}) {
  const summary = useMemo(
    () => summariseLocations(regions ?? [], regionSets, selectedIds),
    [regions, regionSets, selectedIds],
  )
  if (summary.regions.length === 0) {
    return <>{summary.national ? 'Nationally available funding only' : 'No location specified'}</>
  }
  return (
    <>
      {summary.regions.map((node, index) => (
        <Fragment key={index}>{index > 0 ? '; ' : ''}{renderSummaryNode(node)}</Fragment>
      ))}
    </>
  )
}

function itemCountLabel(group: RegionGroup) {
  if (group.type === 'majors') return 'areas'
  if (group.type === 'region_sets') return 'area groups'
  return 'councils'
}

function CheckboxRow({
  checked,
  disabled = false,
  label,
  onChange,
  fontSize = 13,
  focusKey,
  filled = false,
}: {
  checked: boolean
  disabled?: boolean
  label: string
  onChange: (checked: boolean) => void
  fontSize?: number
  focusKey?: string
  /** Fill the row's background while ticked, so selections stand out. */
  filled?: boolean
}) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 24,
        cursor: disabled ? 'default' : 'pointer',
        ...(filled && { px: 0.75, borderRadius: 0.625, bgcolor: checked ? SELECTED_FILL : 'transparent' }),
      }}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        inputProps={focusKey ? { 'data-focus-key': focusKey } as InputHTMLAttributes<HTMLInputElement> : undefined}
        onChange={(_, nextChecked) => onChange(nextChecked)}
        sx={{
          p: 0,
          width: fontSize >= 13 ? 14 : 13,
          height: fontSize >= 13 ? 14 : 13,
          color: '#1C3045',
          '&.Mui-checked': { color: '#1C3045' },
          '& .MuiSvgIcon-root': { fontSize: fontSize >= 13 ? 18 : 17 },
        }}
      />
      <Typography component="span" sx={{ fontSize, color: '#374151', lineHeight: 1.4 }}>
        {label}
      </Typography>
    </Box>
  )
}

// Background of a ticked council, area or suburb; unticked ones are outline only.
const SELECTED_FILL = '#e6eef8'

// Underlined text buttons (Collapse, Remove region, Select all). 24px tall
// at least, so they meet the minimum target size.
const textButtonSx = { minWidth: 0, minHeight: 24, px: 0.5, py: 0, textTransform: 'none' } as const

// Opportunity-count badge used by the in-selector counter (onboarding rail).
// Pass sx to position/space it per context.
function OpportunityCounter({
  count,
  loading,
  sx = {},
}: {
  count?: number
  loading?: boolean
  sx?: object
}) {
  return (
    <Box sx={{ bgcolor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 1.25, px: 2.25, py: 1.5, textAlign: 'center', minWidth: 132, ...sx }}>
      <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading || count === undefined ? (
          <CircularProgress size={22} thickness={5} sx={{ color: '#0369a1' }} aria-label="Loading opportunity count" />
        ) : (
          <Typography sx={{ fontSize: 28, lineHeight: 1, fontWeight: 700, color: '#0369a1' }}>
            {count.toLocaleString()}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#0369a1', lineHeight: 1.35 }}>
        potential funding<br />opportunities
      </Typography>
    </Box>
  )
}

function NationalBanner() {
  return (
    <Box sx={{ bgcolor: '#1C3045', borderRadius: 1.25, px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
        <span aria-hidden="true">✓ </span>New Zealand — nationally available funding opportunities
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 1, px: 1, py: 0.25, whiteSpace: 'nowrap' }}>Always included</Typography>
    </Box>
  )
}

// Search mode leaves the national banner and status line to the page, which
// keeps them visible while the picker itself is collapsed.
export function SearchLocationsStatus({ hasLocalSelection }: { hasLocalSelection: boolean }) {
  return (
    <>
      <NationalBanner />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hasLocalSelection ? (
          <>You're currently matched with funding within the selected areas below, including suburb-specific opportunities. Click <strong>Refine</strong> to review or adjust which parts apply to you.</>
        ) : (
          "You're currently matched with nationally available funding opportunities only. Select where you work to see local grants."
        )}
      </Typography>
    </>
  )
}

/** One label per selected region, with " (refined)" when part of it is unticked. */
export function selectedRegionLabels(
  regions: RegionData[],
  regionSets: RegionSetData[] | undefined,
  selectedIds: number[],
): string[] {
  return summariseLocations(regions, regionSets, selectedIds).regions.map((node) =>
    node.only || node.except || node.someOf ? `${node.name} (refined)` : node.name,
  )
}

export default function LocationSelector({
  regions,
  regionSets = [],
  selectedIds,
  onChange,
  mode,
  showCounter = false,
  opportunityCount,
  opportunityCountLoading = false,
}: LocationSelectorProps) {
  const { nationalRegion, groups } = useMemo(
    () => buildLocationGroups(regions, regionSets),
    [regions, regionSets],
  )
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())
  const [refinedCouncils, setRefinedCouncils] = useState<Set<string>>(new Set())
  const [refinedMajors, setRefinedMajors] = useState<Set<string>>(new Set())
  const [settingsPickerOpen, setSettingsPickerOpen] = useState(false)

  // Toggles that swap themselves out (Refine ↔ Collapse, Remove) would drop
  // keyboard focus to the page. Each names the control to focus next, by its
  // data-focus-key; the first enabled match after the re-render takes focus.
  // A disabled match can't take focus, so it falls through to the next key.
  const rootRef = useRef<HTMLDivElement>(null)
  const [focusNext, setFocusNext] = useState<string[] | null>(null)
  useEffect(() => {
    if (!focusNext) return
    for (const key of focusNext) {
      const target = rootRef.current?.querySelector<HTMLElement>(
        `[data-focus-key="${CSS.escape(key)}"]`,
      )
      if (target && !target.matches(':disabled')) {
        target.focus()
        break
      }
    }
    setFocusNext(null)
  }, [focusNext])
  // After a region goes, its tile in the picker, else whatever opens the picker.
  const afterRegionRemoved = (name: string) => [`region:${name}`, 'add-region', 'add-local-locations', 'add-locations']

  const nationalOnlySelected =
    !!nationalRegion &&
    selectedIds.length === 1 &&
    selectedIds[0] === nationalRegion.id

  const selectedLocalIds = nationalRegion
    ? selectedIds.filter((id) => id !== nationalRegion.id)
    : selectedIds

  function setExpandedRegion(name: string, expanded: boolean) {
    setExpandedRegions((prev) => {
      const next = new Set(prev)
      if (expanded) next.add(name)
      else next.delete(name)
      return next
    })
  }

  function toggleRegion(group: RegionGroup) {
    const groupIds = uniq([...group.directIds, ...group.items.flatMap((item) => item.ids)])
    if (hasAny(selectedIds, idsForGroup(group))) {
      onChange(withoutIds(selectedIds, idsForGroup(group)))
      setExpandedRegion(group.name, false)
    } else {
      onChange(uniq([...withoutIds(selectedIds, nationalRegion ? [nationalRegion.id] : []), ...groupIds]))
      // Flat regions (no councils) have nothing to refine, so don't expand.
      if (group.type !== 'region') setExpandedRegion(group.name, true)
    }
  }

  // Adding an area commits to it, so national and any region-only marker go.
  function addIds(group: RegionGroup, ids: number[]) {
    onChange(uniq([...withoutIds(selectedIds, [...(nationalRegion ? [nationalRegion.id] : []), ...group.regionLevelIds]), ...ids]))
  }

  function removeIds(group: RegionGroup, ids: number[]) {
    onChange(keepRegion(group, withoutIds(selectedIds, ids)))
  }

  function removeMetro(group: RegionGroup) {
    removeIds(group, group.metroIds ?? [])
  }

  function toggleItem(group: RegionGroup, item: LocationItem, checked: boolean) {
    if (checked) addIds(group, item.ids)
    else removeIds(group, item.ids)
  }

  function bulk(group: RegionGroup, checked: boolean) {
    const groupIds = group.items.flatMap((item) => item.ids)
    if (checked) addIds(group, groupIds)
    else removeIds(group, groupIds)
  }

  function removeCouncil(council: CouncilGroup) {
    onChange(withoutIds(selectedIds, idsForCouncil(council)))
  }

  function refineCouncil(councilId: string, refined: boolean) {
    setRefinedCouncils((prev) => {
      const next = new Set(prev)
      if (refined) next.add(councilId)
      else next.delete(councilId)
      return next
    })
  }

  function refineMajor(councilId: string, majorId: string, refined: boolean) {
    const key = `${councilId}:${majorId}`
    setRefinedMajors((prev) => {
      const next = new Set(prev)
      if (refined) next.add(key)
      else next.delete(key)
      return next
    })
  }

  function toggleMajor(council: CouncilGroup, major: LocationItem, checked: boolean) {
    const majorSubtreeIds = idsForMajor(council, major)
    // Removing the council-level id de-selects the other majors' implicit
    // selection, so we re-materialise them — but ONLY the ones that were
    // actually selected (council selected, or that major picked individually).
    // Without this guard, unchecking one major wrongly ticks unselected siblings.
    const councilSelected = hasAny(selectedIds, council.ids)
    const otherMajorIds = council.majors
      .filter((candidate) => candidate.id !== major.id)
      .filter((candidate) => councilSelected || hasAny(selectedIds, idsForMajor(council, candidate)))
      .flatMap((candidate) => candidate.ids)
    const nextBase = withoutIds(selectedIds, [...council.ids, ...majorSubtreeIds])
    // Add the whole major subtree, not just its marker — a major with no ids of
    // its own (an Auckland tile's suburbs heading) would otherwise select nothing.
    onChange(checked ? uniq([...nextBase, ...otherMajorIds, ...majorSubtreeIds]) : uniq([...nextBase, ...otherMajorIds]))
  }

  function toggleSuburb(council: CouncilGroup, major: LocationItem, suburb: LocationItem, checked: boolean) {
    const siblings = suburbsForMajor(council, major)
    const nextBase = withoutIds(selectedIds, [...council.ids, ...major.ids, ...suburb.ids])
    // Removing council.ids also strips the OTHER majors' implicit selection, so
    // re-materialise the ones that were actually selected — otherwise unticking
    // one suburb silently drops every sibling major covered by the council marker.
    const councilSelected = hasAny(selectedIds, council.ids)
    const otherMajorIds = council.majors
      .filter((candidate) => candidate.id !== major.id)
      .filter((candidate) => councilSelected || hasAny(selectedIds, idsForMajor(council, candidate)))
      .flatMap((candidate) => candidate.ids)
    const selectedSiblingIds = siblings
      .filter((candidate) =>
        candidate.id !== suburb.id &&
        (hasAny(selectedIds, council.ids) || hasAny(selectedIds, major.ids) || hasAll(selectedIds, candidate.ids))
      )
      .flatMap((candidate) => candidate.ids)
    onChange(checked
      ? uniq([...nextBase, ...otherMajorIds, ...selectedSiblingIds, ...suburb.ids])
      : uniq([...nextBase, ...otherMajorIds, ...selectedSiblingIds]))
  }

  const activeGroups = groups.filter((group) => hasAny(selectedIds, idsForGroup(group)))
  const showSettingsPicker = settingsPickerOpen || (mode === 'settings' && selectedIds.length === 0)

  // Single reusable counter, rendered via showCounter in any mode (onboarding
  // sticky rail, settings, funding needs). extraSx positions it per context.
  function renderCounter(extraSx: object = {}) {
    if (!showCounter) return null
    return (
      <OpportunityCounter
        count={opportunityCount}
        loading={opportunityCountLoading}
        sx={extraSx}
      />
    )
  }

  function renderRegionPicker(includeSelectedExpansions = true) {
    return (
      <>
        {mode !== 'search' && <NationalBanner />}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          {groups.map((group) => {
            const selected = hasAny(selectedIds, idsForGroup(group))
            return (
              <Button
                key={group.name}
                data-focus-key={`region:${group.name}`}
                variant="outlined"
                onClick={() => toggleRegion(group)}
                aria-pressed={selected}
                sx={{
                  minHeight: 44,
                  borderRadius: 1,
                  borderColor: selected ? '#1C3045' : '#d1d5db',
                  bgcolor: selected ? '#1C3045' : '#fff',
                  color: selected ? '#fff' : '#1C3045',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: selected ? '#1C3045' : '#f8fafc', borderColor: '#1C3045' },
                }}
              >
                {group.name}
              </Button>
            )
          })}
        </Box>

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Detail box keys off group.items (NOT idsForGroup): flat 'region' groups
              have empty items, so they intentionally render no refinement panel.
              Settings NEVER renders detail below the grid — every region's detail
              lives in its own box above (activeGroups). The picker grid here is
              purely for adding regions. Onboarding does show detail inline below. */}
          {groups.filter((group) => {
            if (mode !== 'onboarding') return false
            return expandedRegions.has(group.name) || (includeSelectedExpansions && hasAny(selectedIds, group.items.flatMap((item) => item.ids)))
          }).map((group) => (
            <Box key={group.name} sx={{ border: '1px solid #d1d5db', borderRadius: 1.25, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, color: '#111827' }}>{group.name}</Typography>
                <Button
                  size="small"
                  onClick={() => {
                    onChange(withoutIds(selectedIds, idsForGroup(group)))
                    setExpandedRegion(group.name, false)
                    setFocusNext(afterRegionRemoved(group.name))
                  }}
                  aria-label={`Remove region: ${group.name}`}
                  sx={{ minWidth: 0, p: 0, color: '#4b5563', fontSize: 12, textDecoration: 'underline', textTransform: 'none', '&:hover': { color: '#1C3045' } }}
                >
                  Remove
                </Button>
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                {isRegionLevelOnly(group, selectedIds) && <Box sx={{ mb: 1 }}><RegionOnlyNotice group={group} /></Box>}
                {hasAny(selectedIds, group.metroIds ?? []) && (
                  <Box sx={{ mb: 1 }}>
                    <CheckboxRow checked label="All of metropolitan Auckland" fontSize={14} onChange={() => removeMetro(group)} />
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{group.note}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button size="small" onClick={() => bulk(group, true)} sx={{ textTransform: 'none' }}>Select all</Button>
                  <Button size="small" onClick={() => bulk(group, false)} sx={{ textTransform: 'none' }}>Clear all</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                  {group.items.map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={<Checkbox checked={hasAll(selectedIds, item.ids)} onChange={(_, checked) => toggleItem(group, item, checked)} sx={{ color: '#1C3045', '&.Mui-checked': { color: '#1C3045' } }} />}
                      label={<Typography sx={{ fontSize: 14 }}>{item.name}</Typography>}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">{group.items.length} {itemCountLabel(group)}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </>
    )
  }

  function renderActiveGroups() {
    return (
      <>
            {activeGroups.map((group) => {
            const groupIds = idsForGroup(group)
            // Selected only via a legacy whole-region id (no council/major/suburb
            // committed). Show it honestly and let the user refine to councils.
            const regionLevelOnly = isRegionLevelOnly(group, selectedIds)
            return (
            <Box key={group.name} sx={{ border: '1px solid #d1d5db', borderRadius: 1, mb: 2, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ px: 2, py: 1.25, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{group.name}</Typography>
                {/* Collapse on remove too — otherwise the region stays in
                    expandedRegions and its detail re-renders BELOW the picker
                    grid (detail showing both above and below). */}
                <Button size="small" onClick={() => { onChange(withoutIds(selectedIds, groupIds)); setExpandedRegion(group.name, false); setFocusNext(afterRegionRemoved(group.name)) }} aria-label={`Remove region: ${group.name}`} sx={{ ...textButtonSx, color: '#4b5563', fontSize: 12, textDecoration: 'underline' }}>Remove region</Button>
              </Box>
              {regionLevelOnly && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <RegionOnlyNotice group={group} />
                </Box>
              )}
              {hasAny(selectedIds, group.metroIds ?? []) && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <CheckboxRow
                    checked
                    label="All of metropolitan Auckland"
                    fontSize={14}
                    onChange={() => removeMetro(group)}
                  />
                </Box>
              )}
              {/* Every council, ticked = served. In the region-only state above,
                  all sit unticked so the user adds only the ones they want. */}
              <Box sx={{ p: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, alignItems: 'start' }}>
              {/* Show EVERY council of the region — "all pre-selected, uncheck the
                  ones you don't serve". Selected councils are ticked; the rest sit
                  unticked so the user can add them without re-adding the region. */}
              {group.councils.map((council) => {
                const councilChecked = hasAny(selectedIds, idsForCouncil(council))
                const refined = councilChecked && refinedCouncils.has(council.id)
                // Data-level: council is narrowed — some sub-areas selected but
                // NOT all of them. Shown as a "Refined" tag (collapsed or
                // expanded) so it doesn't look like the whole council is selected.
                // Selecting every area individually is NOT refined.
                const partiallyRefined =
                  hasAny(selectedIds, idsForCouncil(council)) &&
                  !isCouncilFullySelected(council, selectedIds)
                return (
                  <Box key={council.id} sx={{ border: '1px solid', borderColor: councilChecked && !refined ? '#1C3045' : '#e5e7eb', bgcolor: councilChecked && !refined ? SELECTED_FILL : 'transparent', borderRadius: 0.75, overflow: 'hidden', gridColumn: refined ? '1 / -1' : 'auto' }}>
                    {!refined ? (
                      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        {/* Uncheck to remove this council (tickbox is the single
                            include/exclude affordance; explicit Remove is region-level). */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <CheckboxRow
                            checked={councilChecked}
                            label={council.name}
                            focusKey={`check:${council.id}`}
                            fontSize={14}
                            onChange={(checked) => {
                              if (checked) {
                                // Ticking a council commits to it — drop national
                                // and any region-only marker for this region.
                                onChange(uniq([...withoutIds(selectedIds, [...(nationalRegion ? [nationalRegion.id] : []), ...group.regionLevelIds]), ...wholeCouncilIds(council)]))
                              } else {
                                refineCouncil(council.id, false)
                                removeIds(group, idsForCouncil(council))
                              }
                            }}
                          />
                          {partiallyRefined && (
                            <Box component="span" sx={{ color: '#0369a1', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              Refined
                            </Box>
                          )}
                        </Box>
                        <Button
                          disabled={!councilChecked}
                          size="small"
                          data-focus-key={`refine:${council.id}`}
                          aria-label={`Refine ${council.name}`}
                          aria-expanded={false}
                          onClick={() => {
                            refineCouncil(council.id, true)
                            setFocusNext([`collapse:${council.id}`])
                          }}
                          sx={{
                            minWidth: 0,
                            minHeight: 28,
                            px: 1.25,
                            py: 0.375,
                            color: '#1C3045',
                            border: '1px solid #cbd5e1',
                            borderRadius: 0.75,
                            fontSize: 13,
                            lineHeight: 1.2,
                            textTransform: 'none',
                            '&:hover': { borderColor: '#1C3045', bgcolor: '#f8fafc' },
                          }}
                        >
                          Refine <span aria-hidden="true">&nbsp;▾</span>
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ px: 2, py: 1.5, bgcolor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                            {council.name}
                            {partiallyRefined && (
                              <Box component="span" sx={{ ml: 1, color: '#0369a1', fontSize: 12, fontWeight: 700 }}>
                                Refined
                              </Box>
                            )}
                          </Typography>
                          <Button size="small" data-focus-key={`collapse:${council.id}`} aria-label={`Collapse ${council.name}`} aria-expanded onClick={() => { refineCouncil(council.id, false); setFocusNext([`refine:${council.id}`, `check:${council.id}`]) }} sx={{ ...textButtonSx, color: '#4b5563', fontSize: 13, textDecoration: 'underline' }}>Collapse</Button>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Uncheck areas where your organisation doesn't work.</Typography>
                        {council.majors.map((major) => {
                          const key = `${council.id}:${major.id}`
                          const majorRefined = refinedMajors.has(key)
                          const majorChecked = hasAny(selectedIds, council.ids) || hasAny(selectedIds, idsForMajor(council, major))
                          // A town with no suburbs can't be refined further, so it's just a checkbox.
                          const majorHasSuburbs = suburbsForMajor(council, major).length > 0
                          return (
                            <Box key={major.id} sx={{ borderTop: '1px solid #e5e7eb', py: 0.5 }}>
                              {!majorRefined ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.625, borderRadius: 0.625, bgcolor: majorChecked ? SELECTED_FILL : 'transparent', '&:hover': { bgcolor: majorChecked ? '#d9e5f4' : '#f9fafb' } }}>
                                  <CheckboxRow
                                    checked={majorChecked}
                                    label={major.name}
                                    focusKey={`check:${key}`}
                                    onChange={(checked) => toggleMajor(council, major, checked)}
                                  />
                                  {majorHasSuburbs && (
                                  <Button
                                    disabled={!majorChecked}
                                    size="small"
                                    data-focus-key={`refine:${key}`}
                                    aria-label={`Refine ${major.name}`}
                                    aria-expanded={false}
                                    onClick={() => {
                                      refineMajor(council.id, major.id, true)
                                      setFocusNext([`collapse:${key}`])
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      minHeight: 26,
                                      px: 1,
                                      py: 0.25,
                                      color: '#4b5563',
                                      border: '1px solid #e5e7eb',
                                      fontSize: 13,
                                      lineHeight: 1.2,
                                      textTransform: 'none',
                                      '&:hover': { color: '#1C3045', borderColor: '#1C3045', bgcolor: '#fff' },
                                    }}
                                  >
                                    Refine <span aria-hidden="true">&nbsp;▾</span>
                                  </Button>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 0.75, overflow: 'hidden', ml: 2.75, my: 0.375 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 1, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{major.name}</Typography>
                                    <Button size="small" data-focus-key={`collapse:${key}`} aria-label={`Collapse ${major.name}`} aria-expanded onClick={() => { refineMajor(council.id, major.id, false); setFocusNext([`refine:${key}`, `check:${key}`]) }} sx={{ ...textButtonSx, color: '#4b5563', fontSize: 13, textDecoration: 'underline' }}>Collapse</Button>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 2, px: 1, pt: 1 }}>
                                    <Button size="small" aria-label={`Select all in ${major.name}`} onClick={() => toggleMajor(council, major, true)} sx={{ ...textButtonSx, fontSize: 13 }}>Select all</Button>
                                    <Button size="small" aria-label={`Clear all in ${major.name}`} onClick={() => toggleMajor(council, major, false)} sx={{ ...textButtonSx, fontSize: 13 }}>Clear all</Button>
                                  </Box>
                                  <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {suburbsForMajor(council, major).map((suburb) => (
                                      <CheckboxRow
                                        key={suburb.id}
                                        checked={hasAny(selectedIds, council.ids) || hasAny(selectedIds, major.ids) || hasAll(selectedIds, suburb.ids)}
                                        label={suburb.name}
                                        fontSize={12.5}
                                        filled
                                        onChange={(checked) => toggleSuburb(council, major, suburb, checked)}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )
                        })}
                      </Box>
                    )}
                  </Box>
                )
              })}
              </Box>
            </Box>
            )
          })}
      </>
    )
  }

  return (
    <Box ref={rootRef}>
      {mode === 'onboarding' && (
        <>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#1C3045', fontWeight: 700 }}>
                  Where does your work happen?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Select the regions where your organisation delivers work — pick all where you may be eligible. Each region expands so you can refine the specific areas you serve.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Nationally available funding is always included.</strong> Selecting local regions adds more local funding opportunities to your matches.
                </Typography>
              </Box>

              {renderRegionPicker()}

              {selectedLocalIds.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>About your local results</strong><br />
                  You'll see all funding opportunities within your selected areas, including those targeted at specific towns or suburbs. Once you've finished setting up, you can fine-tune each region down to the specific towns or suburbs you serve, any time from your profile settings.
                </Alert>
              )}
            </Box>
            {/* Sticky rail so the count stays visible while narrowing below. */}
            {renderCounter({ position: 'sticky', top: 8, alignSelf: 'flex-start', flexShrink: 0 })}
          </Box>
        </>
      )}

      {mode === 'settings' && (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selectedLocalIds.length > 0 ? (
                <Alert severity="info" sx={{ m: 0 }}>
                  You're currently matched with funding within the selected areas below, including suburb-specific opportunities. Click <strong>Refine</strong> to review or adjust which parts apply to you.
                </Alert>
              ) : nationalOnlySelected ? (
                <Alert severity="info" sx={{ m: 0 }}>
                  You're currently matched with nationally available funding opportunities only. Add local locations if your organisation works in specific areas.
                </Alert>
              ) : null}
            </Box>
            {renderCounter({ flexShrink: 0 })}
          </Box>
          {activeGroups.length === 0 ? (
            <>
              {nationalOnlySelected && (
                <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, mb: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ px: 2, py: 1.25, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>New Zealand</Typography>
                    <Button size="small" data-focus-key="add-local-locations" onClick={() => setSettingsPickerOpen(true)} sx={{ minWidth: 0, p: 0, color: '#1C3045', fontSize: 12, textDecoration: 'underline', textTransform: 'none' }}>Add local locations</Button>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1C3045', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Nationally available funding opportunities only</Typography>
                  </Box>
                </Box>
              )}
              {showSettingsPicker ? (
                <Box sx={{ border: '1px dashed #d1d5db', borderRadius: 1, p: 2, mb: 2, bgcolor: '#fff' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5 }}>Add locations</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
                    Select the regions where you work to add local funding matches. Nationally available funding is always included.
                  </Typography>
                  {renderRegionPicker(false)}
                </Box>
              ) : !nationalOnlySelected && (
                <Box sx={{ border: '1px dashed #d1d5db', borderRadius: 1, p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
                  <Typography color="text.secondary">No local locations set — you'll still match nationally available funding. Add local regions to include local matches.</Typography>
                  <Button variant="outlined" data-focus-key="add-locations" onClick={() => setSettingsPickerOpen(true)} sx={{ color: '#1C3045', borderColor: '#1C3045', textTransform: 'none', flexShrink: 0 }}>Add locations</Button>
                </Box>
              )}
            </>
          ) : (
            <>
            {/* Add another region — pinned ABOVE the selected regions, so picking
                a region from the grid drops its detail in directly below it. */}
            <Box sx={{ mb: 2 }}>
              {showSettingsPicker ? (
                <Box sx={{ border: '1px dashed #d1d5db', borderRadius: 1, p: 2, bgcolor: '#fff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Add another region</Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 13 }}>Selected regions expand so you can uncheck councils or areas you do not serve.</Typography>
                    </Box>
                    <Button size="small" onClick={() => { setSettingsPickerOpen(false); setFocusNext(['add-region']) }} sx={{ minWidth: 0, p: 0, color: '#4b5563', fontSize: 12, textDecoration: 'underline', textTransform: 'none' }}>Collapse</Button>
                  </Box>
                  {renderRegionPicker(false)}
                </Box>
              ) : (
                <Button
                  fullWidth
                  data-focus-key="add-region"
                  variant="outlined"
                  onClick={() => setSettingsPickerOpen(true)}
                  sx={{ border: '2px dashed #d1d5db', color: '#4b5563', py: 1.25, textTransform: 'none', '&:hover': { borderColor: '#1C3045', color: '#1C3045', bgcolor: '#f8fafc' } }}
                >
                  + Add another region
                </Button>
              )}
            </Box>
            {renderActiveGroups()}
            </>
          )}
        </>
      )}

      {mode === 'search' && (
        <>
          <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1.5 }}>
            Select the locations where you work. Selected locations expand so you can uncheck councils or areas you do not serve.
          </Typography>
          {renderRegionPicker(false)}
          {activeGroups.length > 0 && <Box sx={{ mt: 2 }}>{renderActiveGroups()}</Box>}
        </>
      )}
    </Box>
  )
}
